import { useId, useState } from 'react';

type HeroTypingPlaceholderHookResult = ModelHookResult<
	{
		value: string;
		showOverlay: boolean;
		/**
		 * Passed to `HeroTypingOverlay`, and the field's `aria-describedby` target while the overlay is
		 * on: the overlay is aria-hidden, so this carries the hint.
		 */
		descriptionId: string;
	},
	{
		onValueChange: (value: string) => void;
	}
>;

/**
 * Tracks a hero field's value so its typed placeholder overlay can drop away once there is input.
 * The field sets no native placeholder, so one doesn't sit under the typed one.
 *
 * Returns a flag rather than the overlay itself: the overlay lives in `shared/components`, which
 * imports this hook's barrel, so rendering it here would close an import cycle.
 */
export function useHeroTypingPlaceholder(): HeroTypingPlaceholderHookResult {
	const [value, setValue] = useState('');
	const descriptionId = useId();

	// Like a native placeholder, the overlay and its description only show while the field is empty.
	const showOverlay = !value;

	return {
		data: {
			value,
			showOverlay,
			descriptionId,
		},
		actions: { onValueChange: setValue },
	};
}
